import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import {
    computeDomainWarpField,
    type DomainWarpParams,
} from "../../utils/domainWarp";
import { marchingSquares } from "../../utils/marchingSquares";

export interface DomainWarpContourLevel {
    paths: string[];
    level: number;
    threshold: number;
}

interface DomainWarpState {
    // Artboard
    canvasW: number;
    canvasH: number;
    sampleStep: number;
    // Field
    seed: string;
    noiseScale: number;
    warpScale: number;
    warpStrength: number;
    warpIterations: number;
    octaves: number;
    // Contours
    contourLevels: number;
    baseThreshold: number;
    contourStep: number;
    // Appearance
    fillColor: string;
    strokeColor: string;
    bgColor: string;
    strokeWidth: number;
    fillShapes: boolean;
    // Setters
    setCanvasW: (v: number) => void;
    setCanvasH: (v: number) => void;
    setSampleStep: (v: number) => void;
    setSeed: (v: string) => void;
    setNoiseScale: (v: number) => void;
    setWarpScale: (v: number) => void;
    setWarpStrength: (v: number) => void;
    setWarpIterations: (v: number) => void;
    setOctaves: (v: number) => void;
    setContourLevels: (v: number) => void;
    setBaseThreshold: (v: number) => void;
    setContourStep: (v: number) => void;
    setFillColor: (v: string) => void;
    setStrokeColor: (v: string) => void;
    setBgColor: (v: string) => void;
    setStrokeWidth: (v: number) => void;
    setFillShapes: (v: boolean) => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    randomizeParameters: () => void;
    // Derived
    viewBoxAttr: string;
    contourData: DomainWarpContourLevel[];
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
}

const DomainWarpContext = createContext<DomainWarpState | null>(null);

const DEFAULT_DOMAIN_WARP_PRESET: Record<string, unknown> = {
    canvasW: 800,
    canvasH: 600,
    sampleStep: 4,
    seed: "domain",
    noiseScale: 0.003,
    warpScale: 0.003,
    warpStrength: 200,
    warpIterations: 2,
    octaves: 4,
    contourLevels: 8,
    baseThreshold: 0.2,
    contourStep: 0.07,
    fillColor: "#2d5a8e",
    strokeColor: "#1a3a5c",
    bgColor: "#f5f0e8",
    strokeWidth: 0.5,
    fillShapes: true,
};

export function useDomainWarp() {
    const ctx = useContext(DomainWarpContext);
    if (!ctx)
        throw new Error("useDomainWarp must be used within DomainWarpProvider");
    return ctx;
}

function seededRNG(seed: string): () => number {
    let s = [...seed].reduce((a, c) => a + c.charCodeAt(0), 0) || 1;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

export function DomainWarpProvider({ children }: { children: React.ReactNode }) {
    const [canvasW, setCanvasW] = useState(800);
    const [canvasH, setCanvasH] = useState(600);
    const [sampleStep, setSampleStep] = useState(4);
    const [seed, setSeed] = useState("domain");
    const [noiseScale, setNoiseScale] = useState(0.003);
    const [warpScale, setWarpScale] = useState(0.003);
    const [warpStrength, setWarpStrength] = useState(200);
    const [warpIterations, setWarpIterations] = useState(2);
    const [octaves, setOctaves] = useState(4);
    const [contourLevels, setContourLevels] = useState(8);
    const [baseThreshold, setBaseThreshold] = useState(0.2);
    const [contourStep, setContourStep] = useState(0.07);
    const [fillColor, setFillColor] = useState("#2d5a8e");
    const [strokeColor, setStrokeColor] = useState("#1a3a5c");
    const [bgColor, setBgColor] = useState("#f5f0e8");
    const [strokeWidth, setStrokeWidth] = useState(0.5);
    const [fillShapes, setFillShapes] = useState(true);

    const viewBoxAttr = `0 0 ${canvasW} ${canvasH}`;

    const fieldParams = useMemo<DomainWarpParams>(
        () => ({
            canvasW,
            canvasH,
            sampleStep,
            seed,
            noiseScale,
            warpScale,
            warpStrength,
            warpIterations,
            octaves,
        }),
        [
            canvasW,
            canvasH,
            sampleStep,
            seed,
            noiseScale,
            warpScale,
            warpStrength,
            warpIterations,
            octaves,
        ],
    );

    const scalarField = useMemo(
        () => computeDomainWarpField(fieldParams),
        [fieldParams],
    );

    const contourData = useMemo<DomainWarpContourLevel[]>(() => {
        const levels: DomainWarpContourLevel[] = [];
        for (let i = 0; i < contourLevels; i++) {
            const threshold = baseThreshold + i * contourStep;
            const paths = marchingSquares(scalarField, sampleStep, threshold);
            levels.push({ paths, level: i, threshold });
        }
        return levels;
    }, [scalarField, sampleStep, baseThreshold, contourLevels, contourStep]);

    const canExport = contourData.some((l) => l.paths.length > 0);

    const buildExportSvgString = useCallback((): string => {
        const contourPaths = contourData
            .map(({ paths, level }) => {
                const opacity = fillShapes
                    ? ((level + 1) / contourLevels).toFixed(3)
                    : "1";
                return paths
                    .map(
                        (d) =>
                            `<path d="${d}" fill="${fillShapes ? fillColor : "none"}" fill-opacity="${opacity}" stroke="${strokeColor}" stroke-width="${strokeWidth}" />`,
                    )
                    .join("\n    ");
            })
            .join("\n    ");

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <rect width="${canvasW}" height="${canvasH}" fill="${bgColor}" />
  <g class="contours">
    ${contourPaths}
  </g>
</svg>`;
    }, [
        contourData,
        contourLevels,
        fillShapes,
        fillColor,
        strokeColor,
        strokeWidth,
        bgColor,
        canvasW,
        canvasH,
        viewBoxAttr,
    ]);

    const handleDownload = useCallback(() => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `domain-warp-${seed}-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [canExport, buildExportSvgString, seed]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            canvasW, canvasH, sampleStep, seed, noiseScale,
            warpScale, warpStrength, warpIterations, octaves,
            contourLevels, baseThreshold, contourStep,
            fillColor, strokeColor, bgColor, strokeWidth, fillShapes,
        }),
        [
            canvasW, canvasH, sampleStep, seed, noiseScale,
            warpScale, warpStrength, warpIterations, octaves,
            contourLevels, baseThreshold, contourStep,
            fillColor, strokeColor, bgColor, strokeWidth, fillShapes,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.canvasW != null) setCanvasW(values.canvasW as number);
        if (values.canvasH != null) setCanvasH(values.canvasH as number);
        if (values.sampleStep != null) setSampleStep(values.sampleStep as number);
        if (values.seed != null) setSeed(values.seed as string);
        if (values.noiseScale != null) setNoiseScale(values.noiseScale as number);
        if (values.warpScale != null) setWarpScale(values.warpScale as number);
        if (values.warpStrength != null) setWarpStrength(values.warpStrength as number);
        if (values.warpIterations != null) setWarpIterations(values.warpIterations as number);
        if (values.octaves != null) setOctaves(values.octaves as number);
        if (values.contourLevels != null) setContourLevels(values.contourLevels as number);
        if (values.baseThreshold != null) setBaseThreshold(values.baseThreshold as number);
        if (values.contourStep != null) setContourStep(values.contourStep as number);
        if (values.fillColor != null) setFillColor(values.fillColor as string);
        if (values.strokeColor != null) setStrokeColor(values.strokeColor as string);
        if (values.bgColor != null) setBgColor(values.bgColor as string);
        if (values.strokeWidth != null) setStrokeWidth(values.strokeWidth as number);
        if (values.fillShapes != null) setFillShapes(values.fillShapes as boolean);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "domain-warp",
        defaultPreset: DEFAULT_DOMAIN_WARP_PRESET,
        getPresetValues,
        applyPreset,
    });

    const randomizeParameters = useCallback(() => {
        const ts = Date.now().toString();
        const rng = seededRNG(ts);
        setSeed(Math.floor(rng() * 9000 + 1000).toString());
        setNoiseScale(parseFloat((rng() * 0.005 + 0.001).toFixed(4)));
        setWarpScale(parseFloat((rng() * 0.005 + 0.001).toFixed(4)));
        setWarpStrength(Math.round(rng() * 350 + 50));
        setWarpIterations(Math.floor(rng() * 3) + 1);
        setOctaves(Math.floor(rng() * 4) + 2);
        setBaseThreshold(parseFloat((rng() * 0.2 + 0.1).toFixed(2)));
        setContourStep(parseFloat((rng() * 0.08 + 0.03).toFixed(3)));
        setContourLevels(Math.floor(rng() * 8) + 4);
    }, []);

    const value: DomainWarpState = {
        canvasW,
        canvasH,
        sampleStep,
        seed,
        noiseScale,
        warpScale,
        warpStrength,
        warpIterations,
        octaves,
        contourLevels,
        baseThreshold,
        contourStep,
        fillColor,
        strokeColor,
        bgColor,
        strokeWidth,
        fillShapes,
        setCanvasW,
        setCanvasH,
        setSampleStep,
        setSeed,
        setNoiseScale,
        setWarpScale,
        setWarpStrength,
        setWarpIterations,
        setOctaves,
        setContourLevels,
        setBaseThreshold,
        setContourStep,
        setFillColor,
        setStrokeColor,
        setBgColor,
        setStrokeWidth,
        setFillShapes,
        applyPreset,
        getPresetValues,
        resetParameters,
        randomizeParameters,
        viewBoxAttr,
        contourData,
        canExport,
        buildExportSvgString,
        handleDownload,
    };

    return (
        <DomainWarpContext.Provider value={value}>
            {children}
        </DomainWarpContext.Provider>
    );
}
